// Environmental management (ISO 14001). A tabbed register: Waste and resources / Incidents
// (records) and Objectives. New Record / New Objective modals; a row opens its detail page.
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type EnvRecordItem,
  type EnvObjectiveItem,
  type EnvironmentalRecordType,
  type ObjectiveStatus,
} from "../../lib/api";
import { RECORD_TYPE_LABEL, OBJECTIVE_STATUS_LABEL, isIncidentType } from "./environmentalUi";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { assets as assetsResource, type Asset } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

type Tab = "waste" | "incidents" | "objectives";

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "waste", label: "Waste and resources" },
  { value: "incidents", label: "Incidents" },
  { value: "objectives", label: "Objectives" },
];

// Client-side search and sort over the rows each tab already holds (design system Session 7a). The
// search term is the URL's ?q=; it matches only what the table shows. Sort values are the underlying
// facts, never the formatted cell text.
const RECORD_SEARCH: SearchAccessors<EnvRecordItem> = [
  (r) => r.ref,
  (r) => RECORD_TYPE_LABEL[r.recordType] ?? r.recordType,
  (r) => r.description,
  (r) => r.location,
  (r) => r.status,
];
const RECORD_SORT: SortAccessors<EnvRecordItem> = {
  ref: (r) => r.ref,
  type: (r) => RECORD_TYPE_LABEL[r.recordType] ?? r.recordType,
  date: (r) => {
    const t = Date.parse(r.date);
    return Number.isNaN(t) ? null : t;
  },
  description: (r) => (r.description ?? "").toLowerCase(),
  quantity: (r) => r.quantity,
  location: (r) => (r.location ?? "").toLowerCase(),
  status: (r) => (r.status ?? "").toLowerCase(),
};
const RECORD_SORT_OPTIONS = [
  { key: "ref", label: "Reference" },
  { key: "type", label: "Type" },
  { key: "date", label: "Date" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
];
const OBJECTIVE_STATUS_ORDER: Record<string, number> = { on_track: 0, at_risk: 1, achieved: 2, missed: 3 };
const OBJECTIVE_SEARCH: SearchAccessors<EnvObjectiveItem> = [
  (o) => o.ref,
  (o) => o.objective,
  (o) => o.target,
  (o) => o.owner,
  (o) => OBJECTIVE_STATUS_LABEL[o.status] ?? o.status,
];
const OBJECTIVE_SORT: SortAccessors<EnvObjectiveItem> = {
  ref: (o) => o.ref,
  objective: (o) => (o.objective ?? "").toLowerCase(),
  target: (o) => (o.target ?? "").toLowerCase(),
  targetDate: (o) => {
    const t = Date.parse(o.targetDate);
    return Number.isNaN(t) ? null : t;
  },
  owner: (o) => (o.owner ?? "").toLowerCase(),
  status: (o) => OBJECTIVE_STATUS_ORDER[o.status] ?? null,
};
const OBJECTIVE_SORT_OPTIONS = [
  { key: "ref", label: "Reference" },
  { key: "objective", label: "Objective" },
  { key: "targetDate", label: "Target date" },
  { key: "owner", label: "Owner" },
  { key: "status", label: "Status" },
];

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function EnvironmentalRecordsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("waste");
  // Archived records (attached statutory waste evidence, kept for retention) are hidden by default;
  // this toggle lists them so they can be found and reactivated from their detail page.
  const [showArchived, setShowArchived] = useState(false);
  const [records, setRecords] = useState<EnvRecordItem[] | null>(null);
  const [objectives, setObjectives] = useState<EnvObjectiveItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const [addingObjective, setAddingObjective] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // Shared by both tables on this page (records and objectives never render simultaneously).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    api.environmentalRecords
      .list(showArchived ? { archived: true } : {})
      .then(setRecords)
      .catch(() => setLoadError(true));
    api.environmentalObjectives.list().then(setObjectives).catch(() => setLoadError(true));
  }, [showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const wasteRows = (records ?? []).filter((r) => !isIncidentType(r.recordType));
  const incidentRows = (records ?? []).filter((r) => isIncidentType(r.recordType));

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        title="Environmental records"
        className="mb-4"
        subtitle="Waste, incidents and objectives (ISO 14001 evidence)."
        actions={
          tab === "objectives" ? (
            <Button onClick={() => setAddingObjective(true)} icon={<Plus aria-hidden size={15} />}>
              New objective
            </Button>
          ) : (
            <Button onClick={() => setAddingRecord(true)} icon={<Plus aria-hidden size={15} />}>
              New record
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              tab === t.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {t.label}
          </button>
        ))}
        {tab !== "objectives" && (
          <label className="ml-auto flex items-center gap-2 text-xs text-ele-body">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              aria-label="Show archived records"
              className="accent-ele-green"
            />
            Show archived
          </label>
        )}
      </div>

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load environmental records. Please try again.
        </p>
      ) : records === null || objectives === null ? (
        <Loader label="Loading environmental records..." />
      ) : tab === "objectives" ? (
        <ObjectivesTable rows={objectives} onOpen={(id) => navigate(`/office/environmental-objectives/${id}`)} mobileCards={mobileCards} />
      ) : (
        <RecordsTable rows={tab === "waste" ? wasteRows : incidentRows} onOpen={(id) => navigate(`/office/environmental-records/${id}`)} mobileCards={mobileCards} />
      )}

      {addingRecord && (
        <AddRecordModal
          defaultType={tab === "incidents" ? "environmental_incident" : "waste_disposal"}
          onClose={() => setAddingRecord(false)}
          onAdded={(id) => {
            setAddingRecord(false);
            navigate(`/office/environmental-records/${id}`);
          }}
        />
      )}
      {addingObjective && (
        <AddObjectiveModal
          onClose={() => setAddingObjective(false)}
          onAdded={(id) => {
            setAddingObjective(false);
            navigate(`/office/environmental-objectives/${id}`);
          }}
        />
      )}
    </div>
  );
}

function RecordsTable({
  rows,
  onOpen,
  mobileCards,
}: {
  rows: EnvRecordItem[];
  onOpen: (id: string) => void;
  mobileCards: boolean;
}) {
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("environmental-records");
  const shown = sortRows(filterRows(rows, search, RECORD_SEARCH), RECORD_SORT, sort);

  if (rows.length === 0) return <EmptyState message="No records here yet." />;

  const toolbar = (
    <ListToolbar
      className="mb-3"
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search reference, type, description or location..."
      searchLabel="Search environmental records"
      resultCount={{ shown: shown.length, total: rows.length, noun: "records" }}
      sort={{ state: sort, options: RECORD_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
      onClear={() => setSearch("")}
    />
  );

  if (shown.length === 0)
    return (
      <>
        {toolbar}
        <EmptyState role="status" message={`No records match "${search.trim()}".`} />
      </>
    );

  if (mobileCards) {
    // The desktop table hides Type, Description, Quantity and Location below md, leaving only
    // Reference/Date/Status to divide a phone width -- the same truncation shape as the
    // Invoices list this pattern was built for. The card brings the hidden fields back instead
    // of dividing the strip further: Description (the free-text identifying detail) as the
    // second line, Type + Status in the right rail, Quantity/Location in the details block.
    return (
      <>
      {toolbar}
      <MobileCardList label="Environmental records">
        {shown.map((r) => (
          <MobileListCard
            key={r.id}
            onClick={() => onOpen(r.id)}
            primary={
              <>
                {r.ref}
                {r.archivedAt && (
                  <StatusPill className="ml-2" word="Archived" tone="amber" variant="outline" />
                )}
              </>
            }
            secondary={r.description}
            meta={formatDate(r.date)}
            right={
              <>
                <StatusPill word={RECORD_TYPE_LABEL[r.recordType] ?? r.recordType} tone="grey" />
                <span className="text-xs text-ele-body">{r.status || "-"}</span>
              </>
            }
            details={
              r.quantity != null || r.location ? (
                <div className="space-y-1 text-xs text-ele-body">
                  {r.quantity != null && (
                    <p>
                      Quantity: {r.quantity}
                      {r.unit ? ` ${r.unit}` : ""}
                    </p>
                  )}
                  {r.location && <p>Location: {r.location}</p>}
                </div>
              ) : undefined
            }
          />
        ))}
      </MobileCardList>
      </>
    );
  }

  return (
    <>
    {toolbar}
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-left text-sm">
        <colgroup>
          <col style={{ width: "13%" }} />
          <col className="max-md:hidden" style={{ width: "13%" }} />
          <col style={{ width: "11%" }} />
          <col className="max-md:hidden" style={{ width: "28%" }} />
          <col className="max-md:hidden" style={{ width: "12%" }} />
          <col className="max-md:hidden" style={{ width: "13%" }} />
          <col style={{ width: "10%" }} />
          <col className="w-8 md:hidden" />
        </colgroup>
        <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
          <tr>
            <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
            <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
            <SortableHeader label="Date" colKey="date" sort={sort} onToggle={sort.toggle} />
            <SortableHeader label="Description" colKey="description" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Quantity" colKey="quantity" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Location" colKey="location" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
            <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface">
              <td className="px-3 py-2 font-semibold text-ele-strong">
                {r.ref}
                {r.archivedAt && (
                  <StatusPill className="ml-2" word="Archived" tone="amber" variant="outline" />
                )}
              </td>
              <td className="max-md:hidden cell-actions px-3 py-2">
                <StatusPill word={RECORD_TYPE_LABEL[r.recordType] ?? r.recordType} tone="grey" />
              </td>
              <td className="px-3 py-2 text-ele-body">{formatDate(r.date)}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{r.description}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{r.quantity != null ? `${r.quantity}${r.unit ? ` ${r.unit}` : ""}` : "-"}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{r.location || "-"}</td>
              <td className="px-3 py-2 text-ele-body">{r.status || "-"}</td>
              <td className="cell-actions md:hidden pr-1 text-right">
                <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                  <path d="M6 4l4 4-4 4z" />
                </svg>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

function ObjectivesTable({
  rows,
  onOpen,
  mobileCards,
}: {
  rows: EnvObjectiveItem[];
  onOpen: (id: string) => void;
  mobileCards: boolean;
}) {
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("environmental-objectives");
  const shown = sortRows(filterRows(rows, search, OBJECTIVE_SEARCH), OBJECTIVE_SORT, sort);

  if (rows.length === 0) return <EmptyState message="No objectives recorded." />;

  const toolbar = (
    <ListToolbar
      className="mb-3"
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search reference, objective, target or owner..."
      searchLabel="Search environmental objectives"
      resultCount={{ shown: shown.length, total: rows.length, noun: "objectives" }}
      sort={{ state: sort, options: OBJECTIVE_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
      onClear={() => setSearch("")}
    />
  );

  if (shown.length === 0)
    return (
      <>
        {toolbar}
        <EmptyState role="status" message={`No objectives match "${search.trim()}".`} />
      </>
    );

  if (mobileCards) {
    // Objective/Target/Owner are desktop-only below md, leaving Reference/Target date/Status --
    // the card restores Objective as the second line and Target/Owner to the details block.
    return (
      <>
      {toolbar}
      <MobileCardList label="Environmental objectives">
        {shown.map((o) => (
          <MobileListCard
            key={o.id}
            onClick={() => onOpen(o.id)}
            primary={o.ref}
            secondary={o.objective}
            meta={`Target date ${formatDate(o.targetDate)}`}
            right={<StatusPill word={OBJECTIVE_STATUS_LABEL[o.status] ?? o.status} tone={statusEntry("objective", o.status).tone} />}
            details={
              <div className="space-y-1 text-xs text-ele-body">
                <p>Target: {o.target}</p>
                {o.owner && <p>Owner: {o.owner}</p>}
              </div>
            }
          />
        ))}
      </MobileCardList>
      </>
    );
  }

  return (
    <>
    {toolbar}
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-left text-sm">
        <colgroup>
          <col style={{ width: "13%" }} />
          <col className="max-md:hidden" style={{ width: "27%" }} />
          <col className="max-md:hidden" style={{ width: "22%" }} />
          <col style={{ width: "13%" }} />
          <col className="max-md:hidden" style={{ width: "14%" }} />
          <col style={{ width: "11%" }} />
          <col className="w-8 md:hidden" />
        </colgroup>
        <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
          <tr>
            <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
            <SortableHeader label="Objective" colKey="objective" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Target" colKey="target" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Target date" colKey="targetDate" sort={sort} onToggle={sort.toggle} />
            <SortableHeader label="Owner" colKey="owner" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
            <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
            <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {shown.map((o) => (
            <tr key={o.id} onClick={() => onOpen(o.id)} className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface">
              <td className="px-3 py-2 font-semibold text-ele-strong">{o.ref}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{o.objective}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{o.target}</td>
              <td className="px-3 py-2 text-ele-body">{formatDate(o.targetDate)}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{o.owner || "-"}</td>
              <td className="cell-actions px-3 py-2">
                <StatusPill word={OBJECTIVE_STATUS_LABEL[o.status] ?? o.status} tone={statusEntry("objective", o.status).tone} />
              </td>
              <td className="cell-actions md:hidden pr-1 text-right">
                <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                  <path d="M6 4l4 4-4 4z" />
                </svg>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

function AddRecordModal({ defaultType, onClose, onAdded }: { defaultType: EnvironmentalRecordType; onClose: () => void; onAdded: (id: string) => void }) {
  const [form, setForm] = useState({
    recordType: defaultType as EnvironmentalRecordType,
    date: "",
    description: "",
    quantity: "",
    unit: "",
    disposalMethod: "",
    contractor: "",
    contractorRegistrationRef: "",
    wasteCode: "",
    location: "",
    siteId: "",
    assetId: "",
    severity: "",
    actionTaken: "",
    status: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (name: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.date || !form.description.trim()) {
      setErr("Enter a date and a description.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.environmentalRecords.create({
        recordType: form.recordType,
        date: form.date,
        description: form.description.trim(),
        quantity: form.quantity.trim() ? Number(form.quantity) : null,
        unit: form.unit.trim() || null,
        disposalMethod: form.disposalMethod.trim() || null,
        contractor: form.contractor.trim() || null,
        contractorRegistrationRef: form.contractorRegistrationRef.trim() || null,
        wasteCode: form.wasteCode.trim() || null,
        location: form.location.trim() || null,
        siteId: form.siteId || null,
        assetId: form.assetId || null,
        severity: form.severity.trim() || null,
        actionTaken: form.actionTaken.trim() || null,
        status: form.status.trim() || null,
      });
      onAdded(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the record. Please try again.");
      setBusy(false);
    }
  }

  return (
    <FormModalShell title="New environmental record" onClose={onClose} onSubmit={save} busy={busy} err={err} submitLabel="Create record">
      <label>
        <span className={labelCls}>Type</span>
        <select aria-label="Type" value={form.recordType} onChange={set("recordType")} className={inputCls}>
          {(Object.keys(RECORD_TYPE_LABEL) as EnvironmentalRecordType[]).map((t) => (
            <option key={t} value={t}>
              {RECORD_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className={labelCls}>Date</span>
        <input aria-label="Date" type="date" value={form.date} onChange={set("date")} className={inputCls} />
      </label>
      <label className="sm:col-span-2">
        <span className={labelCls}>Description</span>
        <textarea aria-label="Description" value={form.description} onChange={set("description")} rows={2} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Quantity</span>
        <input aria-label="Quantity" type="number" step="0.01" min="0" value={form.quantity} onChange={set("quantity")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Unit</span>
        <input aria-label="Unit" value={form.unit} onChange={set("unit")} placeholder="kg, litres, items" className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Disposal method</span>
        <input aria-label="Disposal method" value={form.disposalMethod} onChange={set("disposalMethod")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Contractor</span>
        <input aria-label="Contractor" value={form.contractor} onChange={set("contractor")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Contractor's waste carrier registration ref</span>
        <input
          aria-label="Contractor's waste carrier registration reference"
          value={form.contractorRegistrationRef}
          onChange={set("contractorRegistrationRef")}
          placeholder="If ELE holds it on file"
          className={inputCls}
        />
      </label>
      <label>
        <span className={labelCls}>Waste code (EWC)</span>
        <input aria-label="Waste code" value={form.wasteCode} onChange={set("wasteCode")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Location</span>
        <input aria-label="Location" value={form.location} onChange={set("location")} className={inputCls} />
      </label>
      <SiteLiftFields
        siteId={form.siteId}
        assetId={form.assetId}
        onSiteChange={(siteId) => setForm((f) => ({ ...f, siteId, assetId: "" }))}
        onAssetChange={(assetId) => setForm((f) => ({ ...f, assetId }))}
      />
      <label>
        <span className={labelCls}>Severity (incidents)</span>
        <input aria-label="Severity" value={form.severity} onChange={set("severity")} placeholder="minor, moderate, significant" className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Status (incidents)</span>
        <input aria-label="Record status" value={form.status} onChange={set("status")} placeholder="open, closed" className={inputCls} />
      </label>
      <label className="sm:col-span-2">
        <span className={labelCls}>Action taken</span>
        <textarea aria-label="Action taken" value={form.actionTaken} onChange={set("actionTaken")} rows={2} className={inputCls} />
      </label>
    </FormModalShell>
  );
}

// The site the disposal relates to, and (optionally, once a site is picked) the specific lift.
// Shared by the create modal and the detail edit panel. `location` (free text, alongside this)
// stays for a record with no exact site/lift, e.g. a workshop clearance.
export function SiteLiftFields({
  siteId,
  assetId,
  onSiteChange,
  onAssetChange,
}: {
  siteId: string;
  assetId: string;
  onSiteChange: (siteId: string) => void;
  onAssetChange: (assetId: string) => void;
}) {
  const assetsLoader = useOptionsLoader<Asset[]>(() => (siteId ? assetsResource.list({ siteId }) : Promise.resolve([])), [siteId]);

  return (
    <>
      <div>
        <RecordPicker kind="site" label="Site" value={siteId} placeholder="Not linked to a site (search to choose)" onChange={(id) => onSiteChange(id)} />
      </div>
      <label>
        <span className={labelCls}>Lift</span>
        <select aria-label="Lift" value={assetId} onChange={(e) => onAssetChange(e.target.value)} className={inputCls} disabled={!siteId}>
          <option value="">{siteId ? "Not linked to a specific lift" : "Choose a site first"}</option>
          {(assetsLoader.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.eleNumber ?? a.legacyEleNumber ?? a.id}
            </option>
          ))}
        </select>
        {assetsLoader.error && (
          <p role="alert" className="mt-1 text-xs text-ele-danger">
            Could not load lifts.{" "}
            <button type="button" onClick={assetsLoader.retry} className="underline">
              Retry
            </button>
          </p>
        )}
      </label>
    </>
  );
}

function AddObjectiveModal({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const [form, setForm] = useState({ objective: "", target: "", targetDate: "", owner: "", status: "on_track" as ObjectiveStatus, progressNotes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (name: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.objective.trim() || !form.target.trim() || !form.targetDate) {
      setErr("Enter the objective, a measurable target and a target date.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.environmentalObjectives.create({
        objective: form.objective.trim(),
        target: form.target.trim(),
        targetDate: form.targetDate,
        owner: form.owner.trim() || null,
        status: form.status,
        progressNotes: form.progressNotes.trim() || null,
      });
      onAdded(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the objective. Please try again.");
      setBusy(false);
    }
  }

  return (
    <FormModalShell title="New environmental objective" onClose={onClose} onSubmit={save} busy={busy} err={err} submitLabel="Create objective">
      <label className="sm:col-span-2">
        <span className={labelCls}>Objective</span>
        <input aria-label="Objective" value={form.objective} onChange={set("objective")} className={inputCls} />
      </label>
      <label className="sm:col-span-2">
        <span className={labelCls}>Measurable target</span>
        <input aria-label="Target" value={form.target} onChange={set("target")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Target date</span>
        <input aria-label="Target date" type="date" value={form.targetDate} onChange={set("targetDate")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Owner</span>
        <input aria-label="Owner" value={form.owner} onChange={set("owner")} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Status</span>
        <select aria-label="Status" value={form.status} onChange={set("status")} className={inputCls}>
          {(Object.keys(OBJECTIVE_STATUS_LABEL) as ObjectiveStatus[]).map((s) => (
            <option key={s} value={s}>
              {OBJECTIVE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </label>
      <label className="sm:col-span-2">
        <span className={labelCls}>Progress notes</span>
        <textarea aria-label="Progress notes" value={form.progressNotes} onChange={set("progressNotes")} rows={2} className={inputCls} />
      </label>
    </FormModalShell>
  );
}

function FormModalShell({
  title,
  onClose,
  onSubmit,
  busy,
  err,
  submitLabel,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  busy: boolean;
  err: string | null;
  submitLabel: string;
  children: ReactNode;
}) {
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={onSubmit}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">{title}</h2>
        <div className="grid gap-3 sm:grid-cols-2">{children}</div>
        {err && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : submitLabel}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
